import { forwardRef, useMemo } from "react";
import { type Column } from "@racoon/ui";
import { useT, Namespace } from "@racoon/i18n";
import {
  StaffKind,
  StaffRegistryRow,
  useStaffRegistryRows,
} from "../model/use-staff-registry-rows";
import { useReportHyperlinks } from "../model/use-report-hyperlinks";
import { compareIds, entityNumber, numberColumn } from "../model/job-report";
import { ReportTable, ReportTableHandle, ReportTableProps } from "./report-table";

const VARIANT = {
  driver: {
    settings: "driver_registry_report",
    title: "reports.driverRegistry",
    fullName: "driverRegistry.fullName",
  },
  loader: {
    settings: "loader_registry_report",
    title: "reports.loaderRegistry",
    fullName: "loaderRegistry.fullName",
  },
} as const;

/** Who the drivers or loaders are and which truck each one was last on. */
const StaffRegistryReportTable = forwardRef<
  ReportTableHandle,
  ReportTableProps & { kind: StaffKind }
>(({ kind, ...props }, ref) => {
  const t = useT();
  const variant = VARIANT[kind];
  const { rows, isLoading, error } = useStaffRegistryRows(kind, props.startDate, props.endDate);
  const { driverCell, plateCell, entityCards } = useReportHyperlinks();

  const columns: Column<StaffRegistryRow>[] = useMemo(
    () => [
      numberColumn(t(Namespace.Analytics, "columns.number")),
      {
        id: "id",
        header: t(Namespace.Analytics, "columns.id"),
        accessor: (row) => entityNumber("user", row.userId),
        width: 100,
        sortable: true,
        sortFn: (a, b) => compareIds(a.userId, b.userId),
        cell: (info) => driverCell(info.row.original.userId, info.getValue() as string),
      },
      {
        id: "name",
        header: t(Namespace.Analytics, variant.fullName),
        accessor: (row) => row.name || "—",
        width: 260,
        sortable: true,
        cell: (info) => driverCell(info.row.original.userId, info.getValue() as string),
      },
      {
        id: "lastShiftTruck",
        header: t(Namespace.Analytics, "driverRegistry.lastShiftTruck"),
        accessor: (row) => row.truckPlateNumber || "—",
        width: 180,
        sortable: true,
        cell: (info) => plateCell(info.row.original.truckId, info.row.original.truckPlateNumber),
      },
    ],
    [t, variant, driverCell, plateCell],
  );

  return (
    <ReportTable
      {...props}
      exportRef={ref}
      settingsKey={variant.settings}
      reportName={t(Namespace.Analytics, variant.title)}
      columns={columns}
      rows={rows}
      rowKey={(row) => String(row.userId)}
      isLoading={isLoading}
      error={error}
    >
      {entityCards}
    </ReportTable>
  );
});

/** «Реестр водителей» (AIS 15.3.2). */
export const DriverRegistryReportTable = forwardRef<ReportTableHandle, ReportTableProps>(
  (props, ref) => <StaffRegistryReportTable ref={ref} {...props} kind="driver" />,
);

/** «Реестр грузчиков» (AIS 15.3.3). */
export const LoaderRegistryReportTable = forwardRef<ReportTableHandle, ReportTableProps>(
  (props, ref) => <StaffRegistryReportTable ref={ref} {...props} kind="loader" />,
);
